import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  Spinner,
  Text,
  LegacyText,
  HStack,
} from "flicket-ui";
import { motion } from "framer-motion";
import { useContext, useMemo, useState } from "react";
import { UserContext } from "~context";
import { useSeatedReservationStore } from "~features/seated-reservation/store";
import * as actions from "~features/seated-reservation/store/actions/index";
import {
  IntegrationType,
  Permission,
  ReferralCampaign,
  TicketTypeKind,
} from "~graphql/sdk";
import { useOrganization, usePermissions } from "~hooks";
import BuyNowPayLater from "../common/common.BuyNowPayLater";
import { AddonListItem } from "./pricingItems/PricingListItemAddon";
import { ListItem } from "./pricingItems/PricingListItem";
import Delayed from "~components/reservation/common/delayed";
import { QuantitySetValidationModal } from "~components/reservation/common/common.QuantitySetValidation.modal";
import { SeatsIOSeat } from "~components/reservation/constants";
import { PaymentPlanBadge } from "../common/common.PaymentPlanBadge";
import { Cart, OrderFee } from "~features/checkout/common/cart";
import { isChilliPeppersFlexiMembershipHack, OrganizationFeatures } from "~lib";
import { useSeekaConverge } from "@seeka-labs/converge-react";
import { formatPrice } from "@flicket/utils";
import { ExpressCheckoutButtonRow } from "~features/checkout/express/components/ExpressCheckoutButtonRow";
import { CreateOrderOptions } from "~hooks/reservation/useCreateOrder";
import { addQueryParams } from "~lib/helpers/queryParams";
import { useRouter } from "next/router";

type QuantitySetValidation = {
  count: number;
  quantitySet: number;
  valid: boolean;
  name: string;
};

export const Pricing = ({
  type,
  changingSeatsFee,
  addons,
  changeTickets,
  navigateCheckout,
  isNavigating,
  onBack,
  bnplGateways,
  priceComp,
  allowPublicComp,
  activeReferralCampaign,
  stripeAfterpayGateway,
  paymentPlanSettings,
  fees,
}: {
  activeReferralCampaign: ReferralCampaign;
  fees: OrderFee;
  navigateCheckout: (options: CreateOrderOptions) => Promise<void>;
} & Partial<React.ComponentProps<any>>) => {
  const converge = useSeekaConverge();
  const { organization, hasFeature } = useOrganization();
  const checkPermissions = usePermissions();
  const { isPOS, isAdmin, user } = useContext(UserContext);
  const router = useRouter();

  const [quantitySetValidationError, setQuantitySetValidationError] = useState<{
    name: string;
    quantitySet: number;
  } | null>(null);
  const noSeatsAvailable = useSeatedReservationStore(
    (state) => state.selectFailed
  );
  const busyState = useSeatedReservationStore((state) => state.busyState);
  const seats = useSeatedReservationStore((state) => state.seats);
  const selectedAddons = useSeatedReservationStore((state) => state.addons);
  const ticketTypes = useSeatedReservationStore(
    (state) => state.eventTicketTypes
  );
  const sidebarIndex = useSeatedReservationStore((state) => state.sidebarIndex);
  const availableTicketTypes = useSeatedReservationStore(
    (state) => state.availableTicketTypes
  );
  const multiBuyPromotions = useSeatedReservationStore(
    (state) => state.multiBuyPromotions
  );

  const flexiMembershipHideFees = isChilliPeppersFlexiMembershipHack(
    organization.id
  );

  let showHiddenFees = hasFeature(OrganizationFeatures.ShowHiddenFees);

  const cart = new Cart(fees, multiBuyPromotions);

  cart.isPos(isPOS);
  cart.setTicketExchangeValue(priceComp ?? 0);
  if (
    !hasFeature(OrganizationFeatures.OrderSmsConfirmationDisabled) &&
    !hasFeature(OrganizationFeatures.OrderSmsConfirmationDefaultOptOut) &&
    !isAdmin &&
    !isPOS
  ) {
    cart.sendSmsConfirmation(true);
  }

  if (flexiMembershipHideFees && priceComp > 0) {
    showHiddenFees = false;
  }

  cart.includeFees(showHiddenFees);

  if (activeReferralCampaign) {
    cart.setReferralCampaign(activeReferralCampaign);
  }

  for (const seat of seats) {
    const id = seat.ticketType?.id || seat.ticketType?.value;

    if (id) {
      cart.add(id as string, seat.originalPrice);
    }
  }

  const hasSeatSelected = seats?.length > 0;
  let totalAddonsSelected = 0;

  for (const [id, quantity] of Object.entries(selectedAddons ?? {})) {
    totalAddonsSelected += quantity;

    const addon = addons.find((a) => a.id === id);

    if (addon?.price) {
      cart.add(id, addon.price, quantity);
    }
  }

  const hasAddonSelected = totalAddonsSelected > 0;

  const validSeatSelection = useSeatedReservationStore(
    (state) => state.validSeatSelection
  );

  const userCredits = user?.credits ?? 0;

  const ticketingOptions = useMemo((): any[] => {
    // Filter entire list of ticket types by permissions
    let filteredTicketTypes =
      (isAdmin && checkPermissions(Permission.OrderComp)) || allowPublicComp
        ? ticketTypes
        : ticketTypes?.filter(({ kind }) => kind === TicketTypeKind.Standard);

    // Filter the above ticket types further based on
    // the current Zone or Section availability
    filteredTicketTypes = filteredTicketTypes?.filter(
      (ticketType) =>
        !!availableTicketTypes?.find(
          ({ id, ticketTypeId, isActive }) =>
            (ticketType?.id === id || ticketType?.id === ticketTypeId) &&
            (typeof isActive !== "undefined" ? isActive : true)
        )
    );

    return filteredTicketTypes?.length
      ? filteredTicketTypes.map(({ id, name, ...rest }) => ({
          label: name,
          value: id,
          ...rest,
        }))
      : [];
  }, [ticketTypes, availableTicketTypes, isAdmin]);

  const availableAddons = useMemo(
    () => addons?.filter(({ isActive }) => isActive),
    [addons]
  );

  const updateSeatType = (seatId, ticketType) => {
    actions.updateSeatType({ seatId, ticketType }, converge);
  };

  const updateAddon = (id, quantity) => {
    actions.updateAddon({ id, quantity });
  };

  let pricingOptions;

  if (busyState === "finding-best-seats") {
    return (
      <motion.div
        key="spinner"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Spinner size={48} color="P300" mb={2} />
      </motion.div>
    );
  }

  if (hasSeatSelected) {
    pricingOptions = seats?.map((seat, i) => (
      <ListItem
        seat={seat}
        handleTicketTypeChange={updateSeatType}
        options={ticketingOptions}
        key={i}
        showHiddenFees={showHiddenFees}
      />
    ));
  } else if (type === "membership") {
    pricingOptions = <LegacyText mb={3}>Please select your seating</LegacyText>;
  } else if (noSeatsAvailable) {
    pricingOptions = (
      <>
        <LegacyText mb={3}>Not enough seats available in this zone</LegacyText>
        <PrimaryButton
          onClick={onBack}
          boxShadow="button"
          mt={{ _: "6/4", md: 1 }}
          w="100%"
        >
          Clear selection
        </PrimaryButton>
      </>
    );
  }

  const validateQuantitySets = (
    seats: SeatsIOSeat[],
    setQuantitySetValidationError: (error: {
      name: string;
      quantitySet: number;
    }) => void
  ): boolean => {
    const validationMap = new Map<string, QuantitySetValidation>();

    for (const { ticketType } of seats) {
      if (!ticketType?.quantitySet) continue;
      const currentEntry = validationMap.get(ticketType.id);
      const currentCount = currentEntry?.count || 0;
      const newCount = currentCount + 1;
      const isValid = newCount % ticketType.quantitySet === 0;

      validationMap.set(ticketType.id, {
        count: newCount,
        valid: isValid,
        quantitySet: ticketType.quantitySet,
        name: ticketType.label ?? ticketType.name,
      });
    }

    for (const entry of validationMap.values()) {
      if (!entry.valid) {
        setQuantitySetValidationError({
          name: entry.name,
          quantitySet: entry.quantitySet,
        });
        return false;
      }
    }

    return true;
  };

  const handleBuyButtonClicked = async (options: CreateOrderOptions = {}) => {
    if (!seats) return;

    const isValid = validateQuantitySets(seats, setQuantitySetValidationError);

    if (isValid) {
      if (options.isExpressCheckout) {
        addQueryParams(router, {
          modal: "express-checkout",
          paymentType: options.paymentType,
        });
      }

      return navigateCheckout(options);
    }
  };

  return (
    <motion.div
      key="prices"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <QuantitySetValidationModal
        isOpen={!!quantitySetValidationError}
        onClose={() => setQuantitySetValidationError(null)}
        quantitySet={quantitySetValidationError?.quantitySet}
        name={quantitySetValidationError?.name}
      />
      {/* Allow extra padding at the bottom to account for mobile safari address bar */}
      <Box pb={10}>
        {sidebarIndex === 3 && (
          <>
            <Flex justifyContent={"space-between"} alignItems="baseline" mb={4}>
              <LegacyText
                fontWeight="heavy"
                fontSize={5}
                color="N800"
                lineHeight="normal"
              >
                Your selection
              </LegacyText>
            </Flex>

            {!hasSeatSelected && (
              <Box mb={2} mt={4}>
                <LegacyText fontWeight="extraBold" fontSize={3} color="N700">
                  No seats selected
                </LegacyText>
              </Box>
            )}
          </>
        )}

        {pricingOptions}

        <Box>
          {!!availableAddons?.length && (
            <Box mb={2} mt={4}>
              <LegacyText
                fontWeight="heavy"
                fontSize={6}
                color="N800"
                lineHeight="normal"
              >
                Available Addons
              </LegacyText>
              <Divider my={2} />
              {availableAddons.map(
                ({
                  id,
                  name,
                  price,
                  description,
                  restrictions,
                  addonImage,
                }) => (
                  <AddonListItem
                    description={description}
                    restrictions={restrictions}
                    isAdmin={isAdmin}
                    addonImage={addonImage}
                    key={id}
                    id={id}
                    name={name}
                    price={price}
                    quantity={selectedAddons?.[id]}
                    onChange={(quanity) => {
                      updateAddon(id, quanity);
                    }}
                  />
                )
              )}
            </Box>
          )}
          {!noSeatsAvailable &&
            (hasSeatSelected || hasAddonSelected || type === "membership") && (
              <>
                {!!changeTickets?.length && (
                  <Box mb={2}>
                    <Divider />
                    {!!changingSeatsFee && (
                      <Flex mt={2} justifyContent="space-between" w="100%">
                        <LegacyText
                          fontWeight="extraBold"
                          fontSize={3}
                          color="N700"
                        >
                          Reservation change fee
                        </LegacyText>
                        <LegacyText
                          fontWeight="medium"
                          fontSize={2}
                          color="N600"
                        >
                          <Price
                            price={changingSeatsFee}
                            currency={organization.currency}
                          />
                        </LegacyText>
                      </Flex>
                    )}
                  </Box>
                )}
                <Divider />
                <Flex mt={2} justifyContent="space-between" w="100%">
                  <LegacyText fontWeight="medium" fontSize={2} color="N600">
                    Total items
                  </LegacyText>
                  <LegacyText
                    fontWeight="medium"
                    fontSize={4}
                    lineHeight={"18px" as any}
                    color={"black" as any}
                  >
                    {seats?.length + totalAddonsSelected}
                  </LegacyText>
                </Flex>

                {cart.multibuyDiscount > 0 && (
                  <>
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <LegacyText fontWeight="medium" fontSize={2} color="N600">
                        Multibuy promotion applied
                      </LegacyText>
                      <Price
                        price={-cart.multibuyDiscount}
                        currency={organization.currency}
                        fontWeight="medium"
                        fontSize={4}
                        lineHeight={"18px" as any}
                        color={"black" as any}
                      />
                    </Flex>
                  </>
                )}

                {cart.referralDiscount > 0 && (
                  <>
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <LegacyText fontWeight="medium" fontSize={2} color="N600">
                        Referral discount applied
                      </LegacyText>
                      <Price
                        price={-cart.referralDiscount}
                        currency={organization.currency}
                        fontWeight="medium"
                        fontSize={4}
                        lineHeight={"18px" as any}
                        color={"black" as any}
                      />
                    </Flex>
                  </>
                )}

                {(cart.multibuyDiscount > 0 || cart.referralDiscount > 0) && (
                  <Divider my={{ _: 3, md: 2 }} />
                )}

                <Flex mt={2} justifyContent="space-between" w="100%">
                  <LegacyText fontWeight="medium" fontSize={2} color="N600">
                    Total {showHiddenFees ? "(including booking fees)" : ""}
                  </LegacyText>
                  <LegacyText
                    fontWeight="medium"
                    fontSize={4}
                    lineHeight={"18px" as any}
                    color={"black" as any}
                  >
                    {seats ? (
                      <Price
                        price={cart.total}
                        currency={organization.currency}
                      />
                    ) : (
                      "-"
                    )}
                  </LegacyText>
                </Flex>
                {!!priceComp && (
                  <>
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      {cart.total < priceComp && !flexiMembershipHideFees && (
                        <Text variant="small">
                          You have selected a lower-priced value.
                          <br />
                          The difference won&apos;t be refunded.
                        </Text>
                      )}
                    </Flex>
                  </>
                )}
                {userCredits > 0 && seats && (
                  <>
                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <LegacyText fontWeight="medium" fontSize={2} color="N600">
                        Your credits to be deducted
                      </LegacyText>
                      <LegacyText
                        fontWeight="medium"
                        fontSize={4}
                        lineHeight={"18px" as any}
                        color={"black" as any}
                      >
                        {`- ${formatPrice(userCredits, {
                          currency: organization.currency,
                        })}`}
                      </LegacyText>
                    </Flex>

                    <Flex mt={2} justifyContent="space-between" w="100%">
                      <LegacyText fontWeight="medium" fontSize={2} color="N600">
                        Total left to pay after deducting credits
                      </LegacyText>
                      <LegacyText
                        fontWeight="medium"
                        fontSize={4}
                        lineHeight={"18px" as any}
                        color={"black" as any}
                      >
                        {cart.total - user.credits > 0 ? (
                          <Price
                            price={cart.total - user.credits}
                            currency={organization.currency}
                          />
                        ) : (
                          "$0.00"
                        )}
                      </LegacyText>
                    </Flex>
                  </>
                )}

                {!isPOS && cart.total - userCredits > 0 && (
                  <>
                    {bnplGateways?.length > 0 &&
                      bnplGateways.map((g) => (
                        <Box mt={2} w="100%" key={g?.type}>
                          <BuyNowPayLater
                            totalPrice={cart.total - userCredits}
                            hasTicketSelected={!!seats.length}
                            showHiddenFees={showHiddenFees}
                            bnplIntegration={g}
                          />
                        </Box>
                      ))}
                    {stripeAfterpayGateway && (
                      <Box
                        justifyContent="center"
                        width="100%"
                        textAlign={"center"}
                        mt={2}
                      >
                        <BuyNowPayLater
                          totalPrice={cart.total - userCredits}
                          hasTicketSelected={!!seats.length}
                          showHiddenFees={showHiddenFees}
                          bnplIntegration={{
                            type: IntegrationType.PaymentAfterpay,
                          }}
                        />
                      </Box>
                    )}
                    {paymentPlanSettings && !paymentPlanSettings.disabledAt && (
                      <Box mt={2}>
                        <PaymentPlanBadge
                          scheduleOptions={paymentPlanSettings?.scheduleOptions}
                        />
                      </Box>
                    )}
                  </>
                )}
                <PrimaryButton
                  boxShadow="button"
                  mt={{ _: "6/4", md: 4 }}
                  onClick={() => {
                    void handleBuyButtonClicked();
                  }}
                  isLoading={isNavigating}
                  disabled={
                    (!hasSeatSelected && !hasAddonSelected) ||
                    (validSeatSelection === false && !isAdmin && !isPOS)
                  }
                  w="100%"
                  fontSize={4}
                >
                  {validSeatSelection === true || isAdmin || isPOS
                    ? hasAddonSelected && !hasSeatSelected
                      ? "Buy addons"
                      : priceComp > 0
                      ? "Exchange tickets"
                      : "Buy tickets"
                    : "Can not leave a single seat"}
                </PrimaryButton>
                <HStack
                  mt={2}
                  justifyContent="flex-end"
                  gap={1}
                  flex={1}
                  width="100%"
                >
                  <ExpressCheckoutButtonRow
                    breakpoint={"xs"}
                    onClick={(method) => {
                      void handleBuyButtonClicked({
                        isExpressCheckout: true,
                        paymentType: method,
                      });
                    }}
                  />
                </HStack>
                {isNavigating && (
                  <Delayed waitBeforeShowMs={1500}>
                    <LegacyText
                      mt={2}
                      fontSize={2}
                      color="N600"
                      textAlign={"center"}
                    >
                      Sit tight, we&apos;re securing your seats...
                    </LegacyText>
                  </Delayed>
                )}
              </>
            )}
        </Box>
      </Box>
    </motion.div>
  );
};
